import { Dialog, Transition } from '@headlessui/react';
import { Fragment, useEffect, useState } from 'react';

import { ReactComponent as CloseIcon } from '@/assets/icons/bx-x.svg';
import { Button } from '@/components/Elements';
import { DateTimePicker } from '@/components/Elements/DatePicker/DateTimePicker';
import { formatYYYYMMDDHHmm } from '@/utils/format';
import { validationEmptyCheck } from '@/utils/inputMultiValidationCheck';

export interface EditModalProps {
  title: string;
  open: boolean;
  initialDate?: Date | null; // 初期表示の日付（登録済みの場合）
  onClose: () => void;
  onSave: (formattedDate: string) => void;
}

export const EditModal = (props: EditModalProps) => {
  const [selectDate, setSelectDate] = useState<Date | null>(props.initialDate || null); // 初期表示に登録済み日付を設定
  const [isDateChanged, setIsDateChanged] = useState(false); // ユーザーが日付を変更したかを追跡
  const [isError, setIsError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    if (props.open && !isDateChanged) {
      setSelectDate(props.initialDate || null);
    }
  }, [props.open, props.initialDate, isDateChanged]);

  const handleSave = (e: React.MouseEvent) => {
    e.preventDefault();

    const strDate = selectDate ? selectDate.toString() : '';
    const isEmpty = validationEmptyCheck(strDate, '投稿予定日');

    if (isEmpty) {
      setIsError(true);
      setErrorMessage(isEmpty);
      return;
    }

    props.onSave(formatYYYYMMDDHHmm(strDate, '-'));
  };

  const handleClose = () => {
    setIsError(false);
    setErrorMessage('');
    props.onClose();
  };

  return (
    <Transition.Root show={props.open} as={Fragment}>
      <Dialog as="div" className="relative z-10" onClose={handleClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-[#6B7280BF]/75 transition-opacity" />
        </Transition.Child>
        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative w-[512px] rounded-lg bg-white p-[24px]">
                <div className="w-[464px] rounded-lg bg-white">
                  <div className="mb-[32px] w-[464px]">
                    <div className="mb-[24px] flex flex-col p-0">
                      <div className="flex flex-row items-center">
                        <div className="flex w-full flex-col">
                          <div className="flex text-center">
                            <span className="text-lg font-medium leading-6">{props.title}</span>
                            <div className="ml-auto">
                              <CloseIcon width={24} height={24} fill="#9CA3AF" onClick={handleClose} />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    {/* カレンダーのUIが崩れるため10px広く */}
                    <div className="w-[330px]">
                      <div className="mb-[4px] h-[20px] w-[70px]">
                        <span className="mb-[4px] text-sm font-semibold leading-5 text-gray-700">投稿予定日</span>
                      </div>
                      <DateTimePicker
                        width="330px"
                        selectedDate={selectDate || undefined}
                        onChange={(date: any) => {
                          setSelectDate(date);
                          setIsDateChanged(true);
                        }}
                        isError={isError}
                      />
                      {isError && <div className="mt-1 text-left text-sm text-red-500">{errorMessage}</div>}
                    </div>
                  </div>
                  <div className="flex flex-row items-center justify-end gap-3">
                    <Button
                      variant="white"
                      size="md"
                      className="h-[38px] w-[104px] whitespace-nowrap text-sm font-medium leading-5"
                      onClick={handleClose}
                    >
                      キャンセル
                    </Button>
                    <Button
                      variant="primary"
                      size="md"
                      className="h-[38px] w-[90px] whitespace-nowrap text-sm font-medium leading-5"
                      onClick={handleSave}
                    >
                      保存する
                    </Button>
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};
